import React, { useCallback, createContext, useContext } from 'react';
import { ModelUtils, TemplatingItem } from '../models';
import { apiHooks } from '../../../hooks/useRestAPIHooks';
import { AxiosError } from 'axios';
import { useMutation } from '@tanstack/react-query';
import { useEventHook } from '../../../hooks/useEventHook';
import { TemplatingItemDetailRawData } from '../types';

type SelectedTemplatingItemContextType = {
  selectedTemplatingItemDurableId: string | null;
  setSelectedTemplatingItemDurableId: (id: string | null) => void;
};

export const SELECTED_TEMPLATING_ITEM_QUERY_KEY =
  'GET /v1/ai-templates/durable/:durableId';

export const SelectedTemplatingItemContext =
  createContext<SelectedTemplatingItemContextType>({
    selectedTemplatingItemDurableId: null,
    setSelectedTemplatingItemDurableId: () => {
      void 0;
    },
  });

type SelectedTemplatingItemProviderProps = {
  children: React.ReactNode;
  selectedTemplatingItemDurableId: string | null;
  setSelectedTemplatingItemDurableId: (id: string | null) => void;
};

export const SelectedTemplatingItemProvider = ({
  children,
  selectedTemplatingItemDurableId,
  setSelectedTemplatingItemDurableId,
}: SelectedTemplatingItemProviderProps) => {
  return (
    <SelectedTemplatingItemContext.Provider
      value={{
        selectedTemplatingItemDurableId: selectedTemplatingItemDurableId,
        setSelectedTemplatingItemDurableId: setSelectedTemplatingItemDurableId,
      }}
    >
      {children}
    </SelectedTemplatingItemContext.Provider>
  );
};

export const useSelectedTemplatingItem = () => {
  const selectedTemplatingItemContext = useContext(
    SelectedTemplatingItemContext,
  );

  const beforeSelectedTemplatingItemChangeEvent = useEventHook({
    key: 'before-selected-templating-item-change',
  });

  const select = useCallback(
    ({
      template: templatingItemDetailRaw,
    }: {
      template: TemplatingItemDetailRawData;
    }) => {
      return ModelUtils.templatingItemDetailFromRawData(
        templatingItemDetailRaw,
      );
    },
    [],
  );

  const selectedTemplatingItemQuery = apiHooks.useAPIQuery(
    SELECTED_TEMPLATING_ITEM_QUERY_KEY,
    {
      durableId:
        selectedTemplatingItemContext.selectedTemplatingItemDurableId ?? '',
    },
    {
      select,
      enabled:
        selectedTemplatingItemContext.selectedTemplatingItemDurableId !== null,
      staleTime: Infinity,
      refetchOnWindowFocus: false,
    },
  );

  const { mutate: setSelectedTemplatingItemId } = useMutation({
    mutationFn: async (id: string | null) => {
      selectedTemplatingItemContext.setSelectedTemplatingItemDurableId(id);
    },
    onMutate: async () => {
      await beforeSelectedTemplatingItemChangeEvent.trigger();
    },
  });

  const isSelected = (templatingItem: TemplatingItem) => {
    return (
      selectedTemplatingItemContext.selectedTemplatingItemDurableId ===
      templatingItem.durableId
    );
  };

  const { error: selectedTemplatingItemError } = selectedTemplatingItemQuery;

  const templatingItemNotFound = isNotFoundError(selectedTemplatingItemError);

  return {
    selectedTemplatingItemId:
      selectedTemplatingItemContext.selectedTemplatingItemDurableId,
    selectedTemplatingItemQuery,
    setSelectedTemplatingItemId,
    isSelected,
    templatingItemNotFound,
    beforeSelectedTemplatingItemChange:
      beforeSelectedTemplatingItemChangeEvent.on,
  };
};

function isNotFoundError(error: unknown) {
  return error instanceof AxiosError && error.response?.status === 404;
}
